import { useState } from 'react';
import './ContactForm.css';
import housePhoto from '../../assets/contact_hero.png';
import logo from '../../assets/logo.svg';

const INITIAL_FORM = { hasSolarPanel: '', name: '', address: '', email: '', phone: '' };
const INITIAL_STATUS = { loading: false, success: false, error: false, message: '' };

export default function ContactForm({ showHeader = false }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState(INITIAL_STATUS);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: false, message: '' });
    setErrors({});

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok) {
        if (typeof window.gtag_report_conversion === 'function') {
          window.gtag_report_conversion();
        }
        setStatus({ loading: false, success: true, error: false, message: data.message });
        setFormData(INITIAL_FORM);
        setTimeout(() => setStatus(prev => ({ ...prev, success: false, message: '' })), 5000);
      } else {
        if (data.errors) setErrors(data.errors);
        setStatus({ loading: false, success: false, error: true, message: data.message || 'Something went wrong. Please try again.' });
      }
    } catch {
      setStatus({ loading: false, success: false, error: true, message: 'Network error. Please check your connection and try again.' });
    }
  };

  return (
    <section className={`cform-section${showHeader ? ' cform-section--enquiry' : ''}`}>
      {/* Full-width background photo */}
      <img src={housePhoto} alt="" className="cform-bg" />
      <div className="cform-overlay" />

      {showHeader && (
        <div className="cform-top-bar">
          <span className="cform-top-logo">
            <img src={logo} alt="Watt & Wattle" className="cform-top-logo-img" />
          </span>
          <div className="cform-top-contact">
            <a
              href="tel:0414339191"
              className="cform-top-link"
              onClick={(e) => {
                if (typeof window.gtag_phone_report_conversion === 'function') {
                  window.gtag_phone_report_conversion('tel:0414339191');
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" fill="rgba(255,255,255,0.85)" />
              </svg>
              0414 339 191
            </a>
            <a href="mailto:sales@wattandwattle.com.au" className="cform-top-link">
              sales@wattandwattle.com.au
            </a>
          </div>
        </div>
      )}

      <div className="cform-inner">
        <div className="cform-content">

          {/* Left — marketing content on dark overlay */}
          <div className="cform-left">
            <div className="cform-left-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#eaff50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h1 className="cform-eyebrow">Contact Us</h1>
            <h2 className="cform-overlay-heading">
              Stop Overpaying<br/>for <span className="cform-overlay-accent">Electricity</span>
            </h2>
            <div className="cform-overlay-divider" />
            <p className="cform-overlay-sub">
              Find out what your home could <span className="cform-overlay-accent">save.</span>
            </p>

            <div className="cform-overlay-badges">
              <div className="cform-overlay-badge">
                <div className="cform-badge-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#eaff50" strokeWidth="1.8" strokeLinecap="round"/>
                    <circle cx="9" cy="7" r="4" stroke="#eaff50" strokeWidth="1.8"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#eaff50" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <p>Trusted by hundreds of VIC households</p>
              </div>
              <div className="cform-overlay-badge">
                <div className="cform-badge-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#eaff50" strokeWidth="1.8"/>
                    <line x1="12" y1="6" x2="12" y2="18" stroke="#eaff50" strokeWidth="1.8" strokeLinecap="round"/>
                    <path d="M8 9.5C8 7.5 9.8 6 12 6s4 1.5 4 3.5S14.2 13 12 13s-4 1.5-4 3.5S9.8 19 12 19s4-1.5 4-3.5" stroke="#eaff50" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <p>With potential savings of up to <span className="cform-overlay-accent">$2,000+ per year.</span></p>
              </div>
            </div>
          </div>

          {/* Right — floating white form card */}
          <div className="cform-panel">
            <h2 className="cform-panel-title">Enquiry Form</h2>
            <p className="cform-panel-sub">Fill in your details and we'll get back to you within 24 hours.</p>

            <form className="cform-form" onSubmit={handleSubmit}>

              <div className="cform-group">
                <label className="cform-label">Have you already installed solar panel?</label>
                <div className="cform-radio-options">
                  <label className="cform-radio-option">
                    <input type="radio" name="hasSolarPanel" value="yes"
                      checked={formData.hasSolarPanel === 'yes'} onChange={handleChange}
                      disabled={status.loading} />
                    <span>Yes</span>
                  </label>
                  <label className="cform-radio-option">
                    <input type="radio" name="hasSolarPanel" value="no"
                      checked={formData.hasSolarPanel === 'no'} onChange={handleChange}
                      disabled={status.loading} />
                    <span>No</span>
                  </label>
                </div>
                {errors.hasSolarPanel && <span className="cform-error">{errors.hasSolarPanel[0]}</span>}
              </div>

              <div className="cform-group">
                <label className="cform-label">Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange}
                  placeholder="Full Name" className={`cform-input${errors.name ? ' cform-input-error' : ''}`}
                  disabled={status.loading} required />
                {errors.name && <span className="cform-error">{errors.name[0]}</span>}
              </div>

              <div className="cform-group">
                <label className="cform-label">Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange}
                  placeholder="Installation address" className={`cform-input${errors.address ? ' cform-input-error' : ''}`}
                  disabled={status.loading} />
                {errors.address && <span className="cform-error">{errors.address[0]}</span>}
              </div>

              <div className="cform-group">
                <label className="cform-label">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange}
                  placeholder="your@email.com" className={`cform-input${errors.email ? ' cform-input-error' : ''}`}
                  disabled={status.loading} required />
                {errors.email && <span className="cform-error">{errors.email[0]}</span>}
              </div>

              <div className="cform-group">
                <label className="cform-label">Phone</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                  placeholder="04XX XXX XXX" className={`cform-input${errors.phone ? ' cform-input-error' : ''}`}
                  disabled={status.loading} />
                {errors.phone && <span className="cform-error">{errors.phone[0]}</span>}
              </div>

              {status.error && (
                <div className="cform-alert cform-alert-error">{status.message}</div>
              )}

              <button type="submit" className="cform-submit-btn" disabled={status.loading}>
                {status.loading ? 'Sending…' : (
                  <>
                    Get free quote
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="#050b14" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>

              {status.success && (
                <div className="cform-alert cform-alert-success">{status.message}</div>
              )}
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}
